import { useEffect, useMemo, useState } from "react";
import { useStep } from "../constants";
import { DatePicker, Form, type FormInstance } from "antd";
import { useNewTrialState } from "./NewTrialStateProvider";
import { useUpdateTrial, useSaveTrial } from "./useTrial";
import useAgOperations from "./useAgOperations";
import {
  type Replicants,
  type TrialPlot,
  type NewTrialStateInterface,
  type allCombineType,
  type BufferZone,
  type MachineryGrid,
  type AbLine,
} from "../types";
import useAxios from "../../../shared/custom-hooks/useAxios";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { addRatesDosagesToPlotProperties } from "./addRatesDosagesToPlotProperties";
import { splitStripGroupsIntoPlots } from "./splitStripPlots";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import { toast } from "react-toastify";
import {
  isValidTrialId,
  ensureValidTrialId,
} from "../../../shared/utils/trialValidation";
import { useTrialsRouter } from "../../../shared/custom-hooks/useTrialsRouter";
import {
  cleanAbLines,
  buildStandardFieldPropertiesPayload,
  mergeStripsDataIntoPayload,
  filterRatesDosagePlotPropertiesForSubStep,
  getRatesTrialTypesWithData,
  getCandidateRatesTrialTypes,
  type RatesTrialType,
} from "../utils";

export interface UseFormHandlingInterface {
  error: string;
  form: FormInstance<any>;
  current: number;
  setCurrent: React.Dispatch<React.SetStateAction<number>>;
  checkValue: boolean;
  setCheckValue: React.Dispatch<React.SetStateAction<boolean>>;
  fieldData: any;
  setFieldData: React.Dispatch<React.SetStateAction<any>>;
  isFieldDataLoading: boolean;
  fieldDataError: string | null | Error;
  RangePicker: any;
  handleFieldSelection: (value: string) => void;
  next: () => void;
  prev: () => void;
  // FIX (Copilot suggestion): changed return type from `any` to `Promise<void>`
  // to accurately reflect the async implementation and enable proper
  // awaited-call error propagation at call sites.
  onFinish: (
    data: any,
    isBackButton?: boolean,
    options?: { skipNavigateOnSummary?: boolean }
  ) => Promise<void>;
  // Saves the Rates & Dosage step's current data when moving between its
  // sub-tabs (Seeds -> CP & Biologicals -> Fertiliser), sending only the
  // sections up to `nextRatesTrialType` so the API never sees an empty,
  // not-yet-filled section. Resolves false (without switching tabs) if the
  // save fails.
  saveRatesDosageSubStep: (
    nextRatesTrialType: RatesTrialType
  ) => Promise<boolean>;
}

const useFormHandling = (): UseFormHandlingInterface => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [, setCheckValue] = useState<boolean>(true);

  const [fieldData, setFieldData] = useState<any>([]);
  const [error, setError] = useState("");

  const {
    current,
    setCurrent,
    newTrialState,
    setTrialInfo,
    trialInfo,
    ratesDosage,
    ratesDosageValidation,
    trialProtocolData,
    isStandardDataValid,
    plotsValidationData,
  } = useNewTrialState();

  const isStripe = !!newTrialState?.stripsData?.isStripMode;
  const Step = useStep(isStripe);

  const { id } = useParams();
  const [trialId, setTrialId] = useState<string | undefined>(id);
  const effectiveTrialId = id ?? trialId ?? trialInfo?.id;
  const { router, orgId, farmId } = useTrialsRouter();
  const updateTrialMutation = useUpdateTrial();
  const saveTrialMutation = useSaveTrial();
  const { handleAgOperation } = useAgOperations();

  const {
    response: farmProperties,
    loading: isFieldDataLoading,
    error: fieldDataError,
  } = useAxios(
    `/properties/${farmId as string}/fields?attributes=geometry`,
    true
  );

  const removeKeysFromObject = (
    obj: allCombineType,
    keysToRemove: string[],
    excludeKeys: string[] = []
  ): any =>
    Object.fromEntries(
      Object.entries(obj).filter(
        ([k]) => !keysToRemove.includes(k) && !excludeKeys.includes(k)
      )
    );

  const modifyTrialPlots = (item: TrialPlot): any => ({
    ...item,
    plot: removeKeysFromObject(item.plot, ["id", "visible", "key"]),
    replicants: item.replicants?.map((replicant: Replicants) =>
      removeKeysFromObject(replicant, ["id", "visible", "key", "parentId"])
    ),
  });

  const removeCollapseValueKey = (obj: any): any => {
    if (Array.isArray(obj)) return obj.map(removeCollapseValueKey);
    if (typeof obj === "object" && obj !== null) {
      const newObj: any = {};
      for (const key in obj) {
        if (key === "collapseValue") continue;
        if (key === "plot_id") {
          newObj[key] = parseInt(obj[key], 10);
        } else {
          newObj[key] = removeCollapseValueKey(obj[key]);
        }
      }
      return newObj;
    }
    return obj;
  };

  const removeKeys = (): NewTrialStateInterface => {
    const modifiedData: NewTrialStateInterface = { ...newTrialState };
    (Object.keys(modifiedData) as Array<keyof NewTrialStateInterface>).forEach(
      (dataKey: keyof NewTrialStateInterface) => {
        const value = modifiedData[dataKey];
        if (Array.isArray(value)) {
          if (dataKey === "ab_line") {
            (modifiedData as any)[dataKey] = (value as AbLine[]).map((i) =>
              removeKeysFromObject(i, ["id", "visible", "key"])
            );
          } else if (dataKey === "buffer_zone") {
            (modifiedData as any)[dataKey] = (value as BufferZone[]).map((i) =>
              removeKeysFromObject(i, ["id", "visible", "key"])
            );
          } else if (dataKey === "machinery_grid") {
            (modifiedData as any)[dataKey] = (value as MachineryGrid[]).map(
              (i) => removeKeysFromObject(i, ["id", "visible", "key"])
            );
          } else if (dataKey === "trial_plots") {
            (modifiedData as any)[dataKey] = (value as TrialPlot[]).map((i) =>
              removeKeysFromObject(i, ["id", "visible", "key"])
            );
          } else if (dataKey === "stripsData") {
            (modifiedData as any)[dataKey] = value;
          }
        }

        if (dataKey === "ab_line") {
          modifiedData[dataKey] = modifiedData[dataKey].map((i: any) =>
            removeKeysFromObject(i, [], ["ticked"])
          );
        }

        if (dataKey === "buffer_zone" || dataKey === "machinery_grid") {
          modifiedData[dataKey] = modifiedData[dataKey].map(
            (i: BufferZone | MachineryGrid) =>
              removeKeysFromObject(i, [], ["name"])
          );
        }

        if (dataKey === "trial_plots") {
          modifiedData[dataKey] = modifiedData[dataKey]?.map(modifyTrialPlots);
        }
      }
    );

    modifiedData.machinery_grid.forEach((mgItem) => {
      if (mgItem) mgItem.rotation = 0;
    });

    return modifiedData;
  };

  function processTrialPlots(trialPlots: any[]): any[] {
    return trialPlots.map((entry) => {
      const { plot, properties } = entry;
      const totalPlots = plot.rows * plot.columns;
      const currentRates = properties?.seeds?.rates_and_dosages;

      if (totalPlots === currentRates?.length) {
        return {
          ...entry,
          properties: {
            ...(properties ?? {}),
          },
        };
      }
      return {
        ...entry,
        properties: {},
      };
    });
  }

  const { RangePicker } = DatePicker;

  const getNext = (cur: number): void => {
    setCurrent(cur + 1);
  };

  const getNextButtonLabel = (cur: number): string => {
    if (cur === Step.length - 1)
      return formatTranslation(t("trials.finishButton"));
    return Step[cur + 1].title;
  };

  const handleFieldSelection = (value: string): void => {
    if (value) form.setFieldsValue({ field_ids: value });
    setTrialInfo({
      ...trialInfo,
      field_ids: [value],
    });
  };

  useEffect(() => {
    if (!form.getFieldValue("field_ids")) {
      form.setFieldsValue({ field_ids: trialInfo?.field_ids?.[0] });
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, [trialInfo?.field_ids?.[0], form]);

  const handleBackRedirection = async (step: number): Promise<void> => {
    if (!trialInfo?.id) {
      console.error(
        formatTranslation(t("common.error.console.navigationBack"))
      );
      return;
    }
    const updatedTrial = await updateTrialMutation.mutateAsync({
      trialId: trialInfo.id as string,
      payload: {
        completion: false,
        completed_step: step,
      },
    });
    if (updatedTrial) setCurrent(step);
  };

  const next = (): void => {
    getNext(current);
    setCheckValue(false);
  };

  const prev = (): void => {
    if (current > 0) {
      handleBackRedirection(current - 1).catch((err) => {
        console.error(
          formatTranslation(t("common.error.console.saveProgressBack")),
          err
        );
      });
    }
    setCheckValue(false);
  };

  /**
   * Validates `effectiveTrialId` for a PATCH-based step. Sets the shared
   * error state (and optionally toasts) and returns null when invalid, so
   * callers can `if (!validTrialId) return;`.
   */
  const getValidTrialIdOrFail = (
    stepLabel: string,
    options?: { toastOnFailure?: boolean }
  ): string | null => {
    if (!isValidTrialId(effectiveTrialId)) {
      const errorMsg =
        formatTranslation(t("common.error.update")) + ": Trial ID is invalid";
      if (options?.toastOnFailure) toast.error(errorMsg);
      setError(errorMsg);
      return null;
    }
    return ensureValidTrialId(effectiveTrialId, stepLabel);
  };

  /**
   * Patches the trial and reports the shared failure state (rewinding
   * `current` back to `failureStep`) when the mutation resolves falsy.
   * Returns the updated trial, or null on failure.
   */
  const patchTrialStep = async ({
    trialId,
    payload,
    failureStep,
    toastOnFailure,
  }: {
    trialId: string;
    payload: any;
    failureStep: number;
    toastOnFailure?: boolean;
  }): Promise<any | null> => {
    const updatedTrial = await updateTrialMutation.mutateAsync({
      trialId,
      payload,
    });

    if (!updatedTrial) {
      setCurrent(failureStep);
      setError(formatTranslation(t("common.error.update")));
      if (toastOnFailure) {
        toast.error(formatTranslation(t("common.error.update")));
      }
      return null;
    }

    return updatedTrial;
  };

  /**
   * Shared catch-handler for the Rates & Dosage / Summary steps: rewinds
   * `current`, surfaces the update error, and logs the original error.
   */
  const handleStepSubmitError = (stepNumber: number, err: unknown): void => {
    setCurrent(stepNumber);
    setError(formatTranslation(t("common.error.update")));
    toast.error(formatTranslation(t("common.error.update")));
    console.error(
      formatTranslation(t("common.error.console.trialUpdate")),
      err
    );
  };

  /**
   * Step 0 — Basic Info. Creates the trial on first save, patches it on
   * every subsequent save, then advances to the next step.
   */
  const submitTrialInfoStep = async (
    data: any,
    isBackButton?: boolean
  ): Promise<void> => {
    const payload = {
      ...data,
      start_date: dayjs(data?.rangePicker[0].$d).format("YYYY-MM-DD"),
      end_date: dayjs(data?.rangePicker[1].$d).format("YYYY-MM-DD"),
      org_id: orgId,
      property_id: farmId,
      field_ids: trialInfo.field_ids,
      completed_step: current + 1,
    };
    delete payload.rangePicker;

    try {
      if (effectiveTrialId) {
        const trialId: string = effectiveTrialId as string;
        const updatedTrial = await updateTrialMutation.mutateAsync({
          trialId,
          payload,
        });
        setTrialInfo({
          ...payload,
          id: updatedTrial?.id,
        });
        await handleAgOperation(
          updatedTrial,
          trialProtocolData?.protocol_type ?? []
        );
      } else {
        const savedTrial = await saveTrialMutation.mutateAsync({ payload });
        setTrialInfo({
          ...payload,
          id: savedTrial?.id,
        });
        if (savedTrial?.id) {
          setTrialId(savedTrial.id as string);
          router.goToTrialsEditPage(undefined, savedTrial.id as string);
        }
      }

      if (!isBackButton) next();
    } catch (err) {
      toast.error(formatTranslation(t("common.error.post")));
      console.error(
        formatTranslation(t("common.error.console.saveBasicInfo")),
        err
      );
    }
  };

  /**
   * Step 1 — Plot Design. No outer try/catch here (matches pre-existing
   * behavior): an unexpected failure propagates to the caller. Returns
   * whether the step succeeded, so `onFinish` knows whether it's safe to
   * advance to the next step.
   */
  const submitPlotDesignStep = async (): Promise<boolean> => {
    const updatedData = removeKeys();
    const processedTrialPlots = processTrialPlots(updatedData.trial_plots);

    let updateApiData: any = {
      ...updatedData,
      standard_field_properties: trialInfo?.standard_field_properties ?? {},
      trial_plots: processedTrialPlots,
      buffer_zone: updatedData.buffer_zone?.length
        ? updatedData.buffer_zone[0]
        : null,
      machinery_grid: updatedData.machinery_grid?.length
        ? updatedData.machinery_grid[0]
        : null,
      completed_step: current + 1,
      ab_line: cleanAbLines(updatedData.ab_line),
    };

    updateApiData = mergeStripsDataIntoPayload(
      updateApiData,
      newTrialState.stripsData,
      t,
      "common.error.console.stripDataOptimization"
    );

    // Backend contract: each strip is its own MultiPolygon plot. The in-app
    // model keeps strips as one group; split into N plots only on the wire.
    updateApiData.trial_plots = splitStripGroupsIntoPlots(
      updateApiData.trial_plots
    );

    const validTrialId = getValidTrialIdOrFail("useFormHandling step 1", {
      toastOnFailure: true,
    });
    if (!validTrialId) return false;

    const updatedTrial = await patchTrialStep({
      trialId: validTrialId,
      payload: updateApiData,
      failureStep: 1,
      toastOnFailure: false,
    });
    if (!updatedTrial) return false;

    await handleAgOperation(
      updatedTrial,
      trialProtocolData?.protocol_type ?? []
    );
    return true;
  };

  /**
   * Shared payload builder for the Rates & Dosage (step 2) and Summary
   * (step 3) steps — everything but `completed_step` is identical between
   * the two.
   */
  const buildRatesDosagePayload = (completedStep: number): any => {
    const updatedData: NewTrialStateInterface = { ...newTrialState };
    const cleanedRatesDosage = removeCollapseValueKey(ratesDosage);

    return {
      ...updatedData,
      buffer_zone: updatedData.buffer_zone[0],
      machinery_grid: updatedData.machinery_grid[0],
      rates_and_dosages: cleanedRatesDosage,
      standard_field_properties:
        buildStandardFieldPropertiesPayload(ratesDosage),
      completed_step: completedStep,
      completion: false,
      ab_line: cleanAbLines(updatedData.ab_line),
    };
  };

  /**
   * Step 2 — Rates & Dosage. Returns whether the step succeeded, so
   * `onFinish` knows whether it's safe to advance to the next step.
   *
   * When `nextRatesTrialType` is provided, this is an intermediate save
   * triggered by moving between the step's own sub-tabs (Seeds -> CP &
   * Biologicals -> Fertiliser) rather than the final save that leaves the
   * step: `completed_step` is left as-is (not advanced), and each plot's
   * payload is filtered down to the sections up to that sub-tab so the API
   * never sees an empty, not-yet-filled section.
   */
  const submitRatesDosageStep = async (
    nextRatesTrialType?: RatesTrialType
  ): Promise<boolean> => {
    let updateApiData = buildRatesDosagePayload(
      nextRatesTrialType ? current : current + 1
    );

    updateApiData = mergeStripsDataIntoPayload(
      updateApiData,
      newTrialState.stripsData,
      t,
      "common.error.console.stripDataSave"
    );

    const transformedUpdateAPIData = addRatesDosagesToPlotProperties(
      updateApiData,
      trialProtocolData
    );
    delete transformedUpdateAPIData.rates_and_dosages;
    transformedUpdateAPIData.trial_plots =
      filterRatesDosagePlotPropertiesForSubStep(
        transformedUpdateAPIData.trial_plots,
        nextRatesTrialType,
        trialProtocolData?.protocol_type
      );

    // Only report the ag operation as covering the rates trial types that
    // actually have data at this point: capped to `nextRatesTrialType` for
    // an intermediate sub-tab save, or to every selected type for the
    // step's final save.
    const candidateRatesTrialTypes = getCandidateRatesTrialTypes(
      trialProtocolData?.protocol_type,
      nextRatesTrialType
    );
    const ratesTrialTypesWithData = getRatesTrialTypesWithData(
      transformedUpdateAPIData.trial_plots,
      candidateRatesTrialTypes
    );

    // Backend contract: each strip is its own MultiPolygon plot. The in-app
    // model keeps strips as one group; split into N plots only on the wire.
    transformedUpdateAPIData.trial_plots = splitStripGroupsIntoPlots(
      transformedUpdateAPIData.trial_plots
    );

    try {
      const validTrialId = getValidTrialIdOrFail("useFormHandling step 2");
      if (!validTrialId) return false;

      const updatedTrial = await patchTrialStep({
        trialId: validTrialId,
        payload: transformedUpdateAPIData,
        failureStep: current,
        toastOnFailure: true,
      });
      if (!updatedTrial) return false;

      await handleAgOperation(updatedTrial, ratesTrialTypesWithData);
      return true;
    } catch (err) {
      handleStepSubmitError(current, err);
      return false;
    }
  };

  /**
   * Called from the UI when the user clicks "Next" on one of the Rates &
   * Dosage sub-tabs (not the step's final save, which goes through
   * `onFinish`/`submitRatesDosageStep()` with no argument).
   */
  const saveRatesDosageSubStep = async (
    nextRatesTrialType: RatesTrialType
  ): Promise<boolean> => await submitRatesDosageStep(nextRatesTrialType);

  /**
   * Step 3 — Summary. Same shape as step 2, but keeps `completed_step` at
   * the current step and navigates away on success (unless the caller
   * skips navigation, e.g. saving from the report drawer). Returns whether
   * the step succeeded, so `onFinish` knows whether it's safe to advance.
   */
  const submitSummaryStep = async (options?: {
    skipNavigateOnSummary?: boolean;
  }): Promise<boolean> => {
    let updateApiData = buildRatesDosagePayload(current);

    updateApiData = mergeStripsDataIntoPayload(
      updateApiData,
      newTrialState.stripsData,
      t,
      "common.error.console.stripDataSave"
    );

    const transformedUpdateAPIData = addRatesDosagesToPlotProperties(
      updateApiData,
      trialProtocolData
    );
    delete transformedUpdateAPIData.rates_and_dosages;

    // Only report the ag operation as covering the rates trial types that
    // actually have data, out of whichever the protocol has selected.
    const candidateRatesTrialTypes = getCandidateRatesTrialTypes(
      trialProtocolData?.protocol_type
    );
    const ratesTrialTypesWithData = getRatesTrialTypesWithData(
      transformedUpdateAPIData.trial_plots,
      candidateRatesTrialTypes
    );

    // Backend contract: each strip is its own MultiPolygon plot. The in-app
    // model keeps strips as one group; split into N plots only on the wire.
    transformedUpdateAPIData.trial_plots = splitStripGroupsIntoPlots(
      transformedUpdateAPIData.trial_plots
    );

    try {
      const validTrialId = getValidTrialIdOrFail("useFormHandling step 3");
      if (!validTrialId) return false;

      const updatedTrial = await patchTrialStep({
        trialId: validTrialId,
        payload: transformedUpdateAPIData,
        failureStep: 3,
        toastOnFailure: true,
      });
      if (!updatedTrial) return false;

      await handleAgOperation(updatedTrial, ratesTrialTypesWithData);
      // Your fix: guard navigation so Summary edit flow can skip it
      if (!options?.skipNavigateOnSummary) {
        router.goToTrialsList();
      }
      return true;
    } catch (err) {
      handleStepSubmitError(3, err);
      return false;
    }
  };

  const onFinish = async (
    data: any,
    isBackButton?: boolean,
    options?: { skipNavigateOnSummary?: boolean }
  ): Promise<void> => {
    if (current === 0) {
      await submitTrialInfoStep(data, isBackButton);
      return;
    }

    let stepSucceeded = true;
    if (current === 1) {
      stepSucceeded = await submitPlotDesignStep();
    } else if (current === 2) {
      stepSucceeded = await submitRatesDosageStep();
    } else if (current === 3) {
      stepSucceeded = await submitSummaryStep(options);
    }

    if (
      stepSucceeded &&
      getNextButtonLabel(current) !==
        formatTranslation(t("trials.finishButton")) &&
      !isBackButton
    ) {
      next();
    }
  };

  /**
   * Step 0 validation (Basic Info) from AntD validateFields
   */
  const values = Form.useWatch([], form);
  const [basicInfoValid, setBasicInfoValid] = useState(false);

  /**
   * following useEffect is used to validate the basic info form fields when the current step is 0. It uses AntD's validateFields method to check if the fields are valid and sets the basicInfoValid state accordingly. The useEffect runs whenever the current step, form instance, or form values change.
   * The useEffect also includes a cancellation mechanism to prevent state updates if the component unmounts or if the effect is re-run before the validation completes. This is done by using a cancelled flag that is checked before updating the state.
   *
   * This ensures that the basic info form is validated in real-time as the user interacts with it, and the validation state is accurately reflected in the UI.
   *
   * @returns {void}
   * @throws {Error} If the form validation fails, the error is caught and the basicInfoValid state is set to false.
   */
  useEffect(() => {
    if (current !== 0) {
      return;
    }

    let cancelled = false;

    const runValidation = async (): Promise<void> => {
      await Promise.resolve();

      if (cancelled) {
        return;
      }

      try {
        await form.validateFields({ validateOnly: true });
        if (!cancelled) {
          setBasicInfoValid(true);
        }
      } catch {
        if (!cancelled) {
          setBasicInfoValid(false);
        }
      }
    };

    void runValidation();

    return () => {
      cancelled = true;
    };
  }, [current, form, values]);

  /**
   * Step 1 validation (Plot Design)
   */
  const plotDesignValid = useMemo(() => {
    const plots = newTrialState.trial_plots ?? [];
    const hasAnyPlots = plots.length > 0;

    const allPlotsHavePlotObject = plots.every(
      (p) => p?.plot && Object.keys(p.plot).length > 0
    );

    if (!hasAnyPlots || !allPlotsHavePlotObject) return false;

    const hasStripPlots = plots.some((p) => p?.plot?.isStripPlot);
    if (hasStripPlots) return true;
    if (!plotsValidationData) return true;

    const outside = plotsValidationData?.polygonsNotInsideField?.length || 0;
    const intersect = plotsValidationData?.intersectingPairs?.length || 0;

    return outside === 0 && intersect === 0;
  }, [newTrialState.trial_plots, plotsValidationData]);

  /**
   * Step 2 validation (Rates & Dosage)
   */
  const ratesValid = useMemo(() => {
    return !!ratesDosageValidation && !!isStandardDataValid;
  }, [ratesDosageValidation, isStandardDataValid]);

  /**
   * Final computed checkValue used by UI.
   * This cannot get stuck after Summary -> Edit -> forward navigation.
   */
  const computedCheckValue = useMemo(() => {
    if (current === 0) return basicInfoValid;
    if (current === 1) return plotDesignValid;
    if (current === 2) return ratesValid;
    if (current === 3) return true;
    return false;
  }, [current, basicInfoValid, plotDesignValid, ratesValid]);

  useEffect(() => {
    setFieldData(farmProperties?.content);
  }, [farmProperties]);

  return {
    error,
    form,
    current,
    setCurrent,
    checkValue: computedCheckValue,
    setCheckValue,
    RangePicker,
    handleFieldSelection,
    fieldData,
    setFieldData,
    isFieldDataLoading,
    fieldDataError,
    next,
    prev,
    onFinish,
    saveRatesDosageSubStep,
  };
};

export default useFormHandling;
